<template lang="html">
  <div class="discover-outfits-container">
    <div class="filters-wrapper">
      <div
        class="sidebar-filter-btn"
        :class="{ active: filter === 'fresh' }"
        @click="changeFilter('fresh')"
      >
        <IconLeaf /><span class="text">Fresh</span>
      </div>

      <div
        class="sidebar-filter-btn"
        :class="{ active: filter === 'hot' }"
        @click="changeFilter('hot')"
      >
        <IconChili /><span class="text">Hot</span>
      </div>
    </div>

    <div class="box-layout-list-container">
      <router-link
        v-for="outfit in discoverOutfits.all"
        :key="outfit.id"
        class="box-layout-list-child"
        tag="a"
        :to="{
          name: 'ShowCapsule',
          params: { capsuleId: outfit.capsule_id },
          query: buildQueryParams(outfit)
        }"
      >
        <div class="box-layout-list-child-inner">
          <div class="title-row">
            <div>
              <div class="title-row--title">{{ outfit.title }}</div>
              <div class="title-row--date">{{ formatDate(outfit.created_at) }}</div>
            </div>

            <div>
              <StitchButton :outfit="outfit" />
            </div>

          </div>
          <BoxLayoutListItem :outfit="outfit" />
        </div>
      </router-link>
    </div>

    <div class="d-flex justify-content-center mb-5">
      <b-button
        variant="light"
        v-if="canLoadMoreOutfits"
        @click="loadMoreOutfits()"
      >
        <span>{{ loadingButtonText }}</span>
        <b-spinner small class="mr-1 ml-2" v-if="loadingData"></b-spinner>
      </b-button>
    </div>
  </div>
</template>

<script>
import BoxLayoutListItem from "@/components/wardrobe/outfits/BoxLayoutListItem";
import CapsuleUtils from "@/utils/CapsuleUtils";
import StitchButton from "@/components/discover/StitchButton";

import IconChili from "@/components/discover/IconChili";
import IconLeaf from "@/components/discover/IconLeaf";

import { mapActions, mapGetters } from "vuex";

export default {
  components: {
    BoxLayoutListItem,
    IconChili,
    IconLeaf,
    StitchButton,
  },

  created() {
    this.formatDate = CapsuleUtils.formatDate;
    this.requestOutfits();
    if (this.currentUser !== null) {
      this.fetchAllStitches();
    }
  },

  mounted() {
    window.analytics.page("Discover Outfits");
    window.addEventListener("scroll", this.infiniteScroll);
  },

  destroy() {
    window.removeEventListener("scroll", this.infiniteScroll);
    this.resetAllStitches();
  },

  data() {
    return {
      filter: "fresh",
      loadingData: true,
      canLoadMoreOutfits: true,
      page: 1,
      per_page: 10,
    };
  },

  computed: {
    ...mapGetters(["currentUser", "discoverOutfits"]),

    loadingButtonText() {
      return this.loadingData ? "Loading Outfits" : "Load more outfits";
    },
  },

  methods: {
    ...mapActions([
      "fetchAllOutfits",
      "fetchAllStitches",
      "resetAllStitches",
      // "resetDiscoverOutfits",
    ]),

    changeFilter(filter) {
      this.filter = filter;
      this.page = 1;
      // this.resetDiscoverOutfits();
      this.loadingData = true;
      this.canLoadMoreOutfits = true;
      this.requestOutfits();
    },

    infiniteScroll() {
      if (window.innerHeight + window.scrollY >= document.body.offsetHeight) {
        if (this.canLoadMoreOutfits && !this.loadingData) {
          this.loadMoreOutfits();
        }
      }
    },

    loadMoreOutfits() {
      this.page = this.discoverOutfits.metaData["next_page"];
      if (!this.loadingData) {
        this.loadingData = true;
        this.requestOutfits();
      }
    },

    requestOutfits() {
      const payload = {
        filter: this.filter,
        page: this.page,
        per_page: this.per_page,
      };

      this.fetchAllOutfits(payload).then(() => {
        this.loadingData = false;
        this.canLoadMoreOutfits = !!this.discoverOutfits.metaData["next_page"];
      });
    },

    buildQueryParams(outfit) {
      return outfit.products.reduce(
        (acc, p) => ({ ...acc, [p.layer_slug]: p.id }),
        {}
      );
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

.discover-outfits-container {
  .filters-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin: 50px auto;
    padding: 10px;

    .sidebar-filter-btn {
      cursor: pointer;
      background-color: white;
      min-width: 50px;
      width: 125px;
      margin-bottom: 0;
      padding: 10px 15px;
      opacity: 0.8;

      &:last-child {
        margin-left: 15px;
      }

      &:hover {
        background-color: $grey8;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);
        opacity: 1;

        .icon {
          &.fresh {
            fill: $emerald;
          }

          &.hot {
            fill: $alizarin;
          }
        }
      }

      &.active {
        opacity: 1;
        background-color: $grey8;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);

        .icon {
          &.fresh {
            fill: $emerald;
          }

          &.hot {
            fill: $alizarin;
          }
        }
      }

      .icon {
        fill: $grey3;
        margin-right: 15px;
        font-size: 16px;
      }

      .text {
        font-size: 14px;
      }
    }
  }
}

.discover-outfits-container {
  .box-layout-list-container {
    --gap: 30px;
    display: flex;
    flex-wrap: wrap;
    flex-direction: column;
    margin: calc(-1 * var(--gap)) 0 0 calc(-1 * var(--gap));
    width: calc(100% + var(--gap));
    justify-content: space-between;

    @include for-tablet-portrait-up {
      flex-direction: row;
    }

    @include for-desktop-up {
      --gap: 50px;
    }
  }

  .box-layout-list-container > * {
    margin: var(--gap) 0 0 var(--gap);
    margin-bottom: 30px;
  }

  .box-layout-list-child {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    max-width: 100%;
    transition: transform 1s;
    align-items: center;
    text-decoration: none;
    color: $grey1;

    @include for-tablet-portrait-up {
      flex: 1 1 calc(50% - var(--gap));
      max-width: calc(50% - var(--gap) / 2);
    }

    .title-row {
      margin-bottom: 25px;
      margin-top: 0px;
      padding-left: 5px;
      display: flex;
      justify-content: space-between;

      .title-row--title {
        font-size: 1.1rem;
        font-weight: 500;
        color: grey1;
      }

      .title-row--date {
        font-size: 0.7rem;
        color: grey4;
        padding-left: 2px;

        &:first-letter {
          text-transform: capitalize;
        }
      }
    }
  }

  .box-layout-list-child-inner {
    display: flex;
    flex-direction: column;
    flex: 1 1 100%;
    padding: 15px;
    background-color: $grey8;
    border: 1px solid $grey5;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);

    &:hover {
      background-color: $grey9;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/discover/DiscoverOutfits.vue